import React, { useMemo } from 'react';
import { View, StyleSheet, useWindowDimensions } from 'react-native';
import ExecutiveOverviewCard from '../../../components/ExecutiveOverviewCard';
import ASNLoadsComparison from './ASNLoadsComparison';
import BottomPerformingNonASN from './BottomPerformingNonASN';
import NonASNLoadTrend from './NonASNLoadTrend';
import { asnOverviewChartsStyles } from '../styles/asnOverviewChartsStyles';
import { PRIMARY_BLUE, WARNING_RED } from '../../../constants/colors';
import {
  ASNBottomPerformingResponse,
  ASNBottomPerformingRow,
  ASNGapLocationHierarchy,
  ASNNonAsnLoadTrendResponse,
  ASNOverviewKpiResponse,
  ASNOverviewKpiStatus,
  LoadsVsNonAsnChartGrandTotal,
  LoadsVsNonAsnChartResponse,
  LoadsVsNonAsnChartSortBy,
} from '../api/asnOverviewKpiApi';
import {
  AsnLoadsChartItem,
  LOADS_VS_NON_ASN_HIERARCHY_LABELS,
  getVisibleLoadsVsNonAsnItems,
  filterChartItemsByValidValues,
} from '../utils/asnLoadsComparisonAdapter';

export type OverviewHierarchyLevel = 'region' | 'market' | 'location';
export type OverviewTimeframeLevel = 'year' | 'period' | 'week';

// Bottom Performing Non-ASN column/subtitle labels, keyed by the API's own
// hierarchyMetadata.currentGrain — kept in sync with the grain the backend
// actually grouped by (rather than the possibly-stale local hierarchyLevel
// prop) so the header never briefly shows the wrong grain after a Chart
// Configuration change while the new response is still in flight.
const BOTTOM_PERFORMING_GRAIN_LABELS: Record<ASNGapLocationHierarchy, string> = {
  REGION: 'Region / Unit',
  MARKET: 'Market / POD',
  LOCATION: 'Location',
};

const CHART_HIERARCHY_TO_GRAIN: Record<OverviewHierarchyLevel, ASNGapLocationHierarchy> = {
  region: 'REGION',
  market: 'MARKET',
  location: 'LOCATION',
};

// Dark neutral value color used for plain-count KPI cards (Non-ASN Loads,
// Total Loads, Non-ASN SKU Count) — matches the color these cards already
// used before the KPI API existed, so a NEUTRAL status never regresses to
// the shared ExecutiveOverviewCard's own default (warning red) fallback.
const NEUTRAL_VALUE_COLOR = '#1A1D21';

/**
 * Maps the backend-evaluated KPI status to the existing card value colors.
 * The backend has already compared `value` against `target` (lower-is-better
 * for % Non-ASN Loads, target 25%), so the card no longer needs — or should
 * use — ExecutiveOverviewCard's built-in `percentage >= 100` heuristic.
 */
export const getKpiValueColor = (status?: ASNOverviewKpiStatus): string | undefined => {
  switch (status) {
    case 'ON_TARGET':
      return PRIMARY_BLUE;
    case 'OFF_TARGET':
      return WARNING_RED;
    case 'NEUTRAL':
    default:
      return NEUTRAL_VALUE_COLOR;
  }
};

interface OverviewProps {
  /** Location hierarchy grain the Overview charts group by — driven by the
   * Overview tab's existing "Chart Configuration" control on AsnGapDashboard.
   * Defaults to 'region' (Region / Unit) when not supplied. */
  hierarchyLevel?: OverviewHierarchyLevel;
  /** Forwarded to BottomPerformingNonASN row presses — wired by
   * AsnGapDashboard to the existing tab-switch (Location Summary) drill-down
   * flow. (The "ASN Loads vs Non-ASN Loads" chart no longer navigates to
   * another tab — see `onLoadsVsNonAsnBarPress` below.) */
  onRowPress?: (item: ASNBottomPerformingRow) => void;
  /** POST /api/asn/overview/kpis response — owned/fetched by AsnGapDashboard
   * (initial load, Apply, Reset) and passed down so there is a single request
   * owner. Undefined/null renders every card's value as "—". */
  kpiData?: ASNOverviewKpiResponse | null;
  /** True while the KPI request is in flight — shows the existing card
   * skeletons on all five cards without changing their layout. */
  kpiLoading?: boolean;
  /** POST /api/asn/bottom-performing-non-asn response — owned/fetched by
   * AsnGapDashboard (initial load, every filter dropdown change, Apply,
   * Reset, and Chart Configuration → Location Hierarchy changes) and passed
   * down so there is a single request owner. Undefined/null renders the
   * card's empty state. */
  bottomPerformingData?: ASNBottomPerformingResponse | null;
  /** True while the Bottom Performing request is in flight — shows the
   * card's existing table skeleton. */
  bottomPerformingLoading?: boolean;
  /** Set when the Bottom Performing request fails — shows the card's
   * existing error state instead of falling back to fixture data. */
  bottomPerformingError?: string | null;
  bottomPerformingSortColumn?: string;
  bottomPerformingSortDirection?: 'ASC' | 'DESC';
  onBottomPerformingSortChange?: (column: string, direction: 'ASC' | 'DESC') => void;
  onBottomPerformingRetry?: () => void;
  /** Chart Configuration -> Timeframe selection driving the Non-ASN Load %
   * Trend chart's title/subtitle and X-axis interval formatting. Defaults
   * to 'period' (the page-load default) when not supplied. */
  timeframeLevel?: OverviewTimeframeLevel;
  /** POST /api/asn/trend/non-asn-load-trend response — owned/fetched by
   * AsnGapDashboard (initial load, Apply, Reset, and Chart Configuration ->
   * Timeframe changes) and passed down so there is a single request owner.
   * Undefined/null renders the chart's empty state. */
  trendData?: ASNNonAsnLoadTrendResponse | null;
  /** True while the trend request is in flight — shows the chart's existing
   * loading skeleton. */
  trendLoading?: boolean;
  /** Set when the trend request fails — shows the chart's existing error
   * state instead of falling back to fixture data. */
  trendError?: string | null;
  onTrendRetry?: () => void;

  // ─── "ASN Loads vs Non-ASN Loads" chart (POST /asn/loads-vs-non-asn/chart) ───
  /** Response for the currently-requested `locationHierarchy` grain (mapped
   * from `hierarchyLevel`: region -> REGION, market -> MARKET, location ->
   * LOCATION) — owned/fetched by AsnGapDashboard, which re-fetches
   * immediately whenever `hierarchyLevel` changes; the visible rows for the
   * current drill path are derived from the response locally (never a
   * separate request). */
  loadsVsNonAsnData?: LoadsVsNonAsnChartResponse | null;
  loadsVsNonAsnLoading?: boolean;
  loadsVsNonAsnError?: string | null;
  /** Selected Region (from a Region bar click or direct Chart Configuration
   * selection) — owned by AsnGapDashboard, not duplicated here. */
  drillRegion?: string | null;
  /** Selected Market (from a Market bar click) — owned by AsnGapDashboard. */
  drillMarket?: string | null;
  /** Authoritative Market/Location id list for the current drill level,
   * sourced from the ASN location dropdown API (the same Region -> Market
   * -> Location relationship the shared filter dropdowns use) — owned/
   * fetched by AsnGapDashboard, independent of the main filter dropdowns.
   * `null` at the Region level (no cross-reference needed); an empty array
   * means the dropdown relationship found nothing for the current drill
   * path (renders the chart's existing empty state instead of any bar). */
  drillOptionValues?: string[] | null;
  /** True while the drill option list above is being (re)fetched. */
  drillOptionsLoading?: boolean;
  /** Fired when a Region or Market bar is pressed — drills down within the
   * chart (never navigates to another tab). */
  onLoadsVsNonAsnBarPress?: (item: AsnLoadsChartItem) => void;
  /** Steps back up one hierarchy level (Location -> Market -> Region). */
  onLoadsVsNonAsnBack?: () => void;
  onLoadsVsNonAsnNavigateToLevel?: (level: OverviewHierarchyLevel, region: string | null, market: string | null) => void;
  loadsVsNonAsnSortBy?: LoadsVsNonAsnChartSortBy;
  onLoadsVsNonAsnSortChange?: (sortBy: LoadsVsNonAsnChartSortBy) => void;
  loadsVsNonAsnSearchQuery?: string;
  onLoadsVsNonAsnSearchChange?: (query: string) => void;
  onLoadsVsNonAsnRetry?: () => void;
}

const Overview: React.FC<OverviewProps> = ({
  hierarchyLevel = 'region',
  onRowPress,
  kpiData,
  kpiLoading = false,
  bottomPerformingData,
  bottomPerformingLoading = false,
  bottomPerformingError = null,
  bottomPerformingSortColumn,
  bottomPerformingSortDirection,
  onBottomPerformingSortChange,
  onBottomPerformingRetry,
  timeframeLevel = 'period',
  trendData,
  trendLoading = false,
  trendError = null,
  onTrendRetry,
  loadsVsNonAsnData,
  loadsVsNonAsnLoading = false,
  loadsVsNonAsnError = null,
  drillRegion = null,
  drillMarket = null,
  drillOptionValues = null,
  drillOptionsLoading = false,
  onLoadsVsNonAsnBarPress,
  onLoadsVsNonAsnBack,
  onLoadsVsNonAsnNavigateToLevel,
  loadsVsNonAsnSortBy = 'totalLoads',
  onLoadsVsNonAsnSortChange,
  loadsVsNonAsnSearchQuery = '',
  onLoadsVsNonAsnSearchChange,
  onLoadsVsNonAsnRetry,
}) => {
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < 768;

  const percentNonAsnLoads = kpiData?.percentNonAsnLoads;
  const nonAsnLoads = kpiData?.nonAsnLoads;
  const totalLoads = kpiData?.totalLoads;
  const nonAsnSkuCount = kpiData?.nonAsnSkuCount;
  const sitesAt100PercentNonAsn = kpiData?.sitesAt100PercentNonAsn;

  // Prefer the grain the backend actually grouped by (from this response);
  // fall back to the local Chart Configuration selection before the first
  // response arrives so the header/column never render blank.
  const bottomPerformingGrain =
    bottomPerformingData?.hierarchyMetadata?.currentGrain ?? CHART_HIERARCHY_TO_GRAIN[hierarchyLevel];
  const bottomPerformingHierarchyLabel = BOTTOM_PERFORMING_GRAIN_LABELS[bottomPerformingGrain];

  // The Loads vs Non-ASN chart's currently-visible rows are derived, purely
  // on the client, from the single flat response already fetched for the
  // current `hierarchyLevel` grain — a `hierarchyLevel` change itself
  // triggers a fresh fetch (owned by AsnGapDashboard), so this only scopes
  // that response to the active drill path (`drillRegion`/`drillMarket`)
  // without an additional request. At the Market/Location levels, the flat
  // rows are additionally cross-referenced against `drillOptionValues` (the
  // authoritative Region -> Market -> Location relationship from the ASN
  // location dropdown API) so a bar is only ever shown for a value that
  // relationship actually recognizes.
  const rawLoadsVsNonAsnItems = useMemo(
    () => getVisibleLoadsVsNonAsnItems(loadsVsNonAsnData?.rows, hierarchyLevel, drillRegion, drillMarket),
    [loadsVsNonAsnData, hierarchyLevel, drillRegion, drillMarket],
  );
  const loadsVsNonAsnItems = useMemo(
    () =>
      hierarchyLevel === 'region'
        ? rawLoadsVsNonAsnItems
        : filterChartItemsByValidValues(rawLoadsVsNonAsnItems, drillOptionValues),
    [rawLoadsVsNonAsnItems, hierarchyLevel, drillOptionValues],
  );
  const loadsVsNonAsnHierarchyLabel = LOADS_VS_NON_ASN_HIERARCHY_LABELS[hierarchyLevel];

  // Build a synthetic AsnLoadsChartItem for the PBNA Total summary bar directly
  // from the chart API's grandTotal field — the authoritative filter-scoped aggregate.
  // Never computed from displayed rows (which shift with drill-down/sorting) and never
  // sourced from kpiData (a different endpoint with different scope).
  const loadsVsNonAsnGrandTotal = useMemo((): AsnLoadsChartItem | undefined => {
    const gt: LoadsVsNonAsnChartGrandTotal | undefined = loadsVsNonAsnData?.grandTotal;
    if (!gt) return undefined;
    return {
      id: 'pbna-total',
      label: 'PBNA Total',
      nonASNLoads: gt.nonAsnLoads,
      asnLoads: gt.asnLoads,
      totalLoads: gt.totalLoads,
      nonASNPercentage: gt.pctNonAsn,
      targetPercentage: gt.targetPct,
      isAboveTarget: gt.isAboveTarget,
      hierarchyLevel: 0,
      grainVals: {},
    };
  }, [loadsVsNonAsnData?.grandTotal]);

  return (
    <View style={styles.container}>
      <View style={styles.kpiRow}>
        <ExecutiveOverviewCard
          title={percentNonAsnLoads?.label ?? '% Non-ASN Loads'}
          value={percentNonAsnLoads?.dataError ? '—' : percentNonAsnLoads?.displayValue ?? '—'}
          subText={percentNonAsnLoads?.dataError ? '' : percentNonAsnLoads?.subLine ?? ''}
          tooltip={percentNonAsnLoads?.infoTooltip}
          tooltipTrigger={isMobile ? 'click' : 'hover'}
          loading={kpiLoading}
          valueColor={getKpiValueColor(percentNonAsnLoads?.status)}
        />
        <ExecutiveOverviewCard
          title={nonAsnLoads?.label ?? 'Non-ASN Loads'}
          value={nonAsnLoads?.dataError ? '—' : nonAsnLoads?.displayValue ?? '—'}
          subText={nonAsnLoads?.dataError ? '' : nonAsnLoads?.subLine ?? ''}
          tooltip={nonAsnLoads?.infoTooltip}
          tooltipTrigger={isMobile ? 'click' : 'hover'}
          loading={kpiLoading}
          valueColor={getKpiValueColor(nonAsnLoads?.status)}
        />
        <ExecutiveOverviewCard
          title={totalLoads?.label ?? 'Total Loads'}
          value={totalLoads?.dataError ? '—' : totalLoads?.displayValue ?? '—'}
          subText={totalLoads?.dataError ? '' : totalLoads?.subLine ?? ''}
          tooltip={totalLoads?.infoTooltip}
          tooltipTrigger={isMobile ? 'click' : 'hover'}
          loading={kpiLoading}
          valueColor={getKpiValueColor(totalLoads?.status)}
        />
        <ExecutiveOverviewCard
          title={nonAsnSkuCount?.label ?? 'Non-ASN SKU Count'}
          value={nonAsnSkuCount?.dataError ? '—' : nonAsnSkuCount?.displayValue ?? '—'}
          subText={nonAsnSkuCount?.dataError ? '' : nonAsnSkuCount?.subLine ?? ''}
          tooltip={nonAsnSkuCount?.infoTooltip}
          tooltipTrigger={isMobile ? 'click' : 'hover'}
          loading={kpiLoading}
          valueColor={getKpiValueColor(nonAsnSkuCount?.status)}
        />
        <ExecutiveOverviewCard
          title={sitesAt100PercentNonAsn?.label ?? 'Sites At 100% Non-ASN'}
          value={sitesAt100PercentNonAsn?.dataError ? '—' : sitesAt100PercentNonAsn?.displayValue ?? '—'}
          subText={sitesAt100PercentNonAsn?.dataError ? '' : sitesAt100PercentNonAsn?.subLine ?? ''}
          tooltip={sitesAt100PercentNonAsn?.infoTooltip}
          tooltipTrigger={isMobile ? 'click' : 'hover'}
          loading={kpiLoading}
          valueColor={getKpiValueColor(sitesAt100PercentNonAsn?.status)}
        />
      </View>

      <View style={asnOverviewChartsStyles.lowerSectionRow}>
        <View style={asnOverviewChartsStyles.lowerSectionColumn}>
          <ASNLoadsComparison
            hierarchyLevel={hierarchyLevel}
            hierarchyLabel={loadsVsNonAsnHierarchyLabel}
            rows={loadsVsNonAsnItems}
            grandTotal={loadsVsNonAsnGrandTotal}
            canGoBack={hierarchyLevel !== 'region'}
            onBack={onLoadsVsNonAsnBack}
            onNavigateToLevel={onLoadsVsNonAsnNavigateToLevel}
            drillRegion={drillRegion}
            drillMarket={drillMarket}
            onBarPress={onLoadsVsNonAsnBarPress}
            sortBy={loadsVsNonAsnSortBy}
            onSortChange={onLoadsVsNonAsnSortChange}
            searchQuery={loadsVsNonAsnSearchQuery}
            onSearchChange={onLoadsVsNonAsnSearchChange}
            onRetry={onLoadsVsNonAsnRetry}
            loading={loadsVsNonAsnLoading || (hierarchyLevel !== 'region' && drillOptionsLoading)}
            error={loadsVsNonAsnError}
            message={loadsVsNonAsnData?.message}
          />
        </View>
        <View style={asnOverviewChartsStyles.lowerSectionColumn}>
          <BottomPerformingNonASN
            hierarchyLabel={bottomPerformingHierarchyLabel}
            rows={bottomPerformingData?.rows ?? []}
            footerText={bottomPerformingData?.footerMetadata?.footerText}
            loading={bottomPerformingLoading}
            error={bottomPerformingError}
            sortColumn={bottomPerformingSortColumn}
            sortDirection={bottomPerformingSortDirection}
            onSortChange={onBottomPerformingSortChange}
            onRetry={onBottomPerformingRetry}
            onRowPress={onRowPress}
            isMobile={isMobile}
          />
        </View>
      </View>

      <NonASNLoadTrend
        trendPoints={trendData?.trendPoints ?? []}
        targetPercentage={trendData?.targetPercentage}
        timeframeGrain={timeframeLevel}
        message={trendData?.message}
        loading={trendLoading}
        error={trendError}
        onRetry={onTrendRetry}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    rowGap: 16,
  },
  kpiRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
});

export default Overview;
